import React from 'react';
import { PropTypes } from 'prop-types';
import { FieldGroup } from './FieldGroup';
import { CheckboxControl } from './Controls/CheckboxControl';
import { ProgressSpinner } from '../progress-spinner/';
import { i18n } from '../../i18n/translate';
import { getPageSize } from '../../utils/pageSize';
import { getData } from '../../json-api/JsonApiUtils';
import { isEqual, set } from 'lodash';
import { selectAll, getTotalCount } from '../../utils';
import { SearchComponent } from '../../components/Form/SearchComponent';
import { Checkbox } from './Controls/Checkbox/Checkbox';
import { Pagination } from '../../core/components/Pagination';
import * as Core from '../../core';

export class AsyncCheckboxList extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    heading: PropTypes.string,
    headingLevel: PropTypes.number,
    showSelectOptions: PropTypes.bool,
    disabled: PropTypes.bool,
    dataUrl: PropTypes.string,
    dataInclude: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    dataObjectAction: PropTypes.string,
    dataObjectAttribute: PropTypes.string,
    dataObjectType: PropTypes.string,
    dataValue: PropTypes.string,
    authToken: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onRadioChange: PropTypes.func,
    labelFunction: PropTypes.func,
    relationKey: PropTypes.string,
    filters: PropTypes.object,
    filterField: PropTypes.string,
    renderClearFilters: PropTypes.func,
    renderRow: PropTypes.func, // (checkboxElement, fieldGroupName, disabled, value, key, isSelected)
    defaultRelationData: PropTypes.func,
    pageSize: PropTypes.number,
    queryFields: PropTypes.object,
    maxPageButtons: PropTypes.number,
    sortBy: PropTypes.string,
    CheckboxComponent: PropTypes.func,
    dataEntity: PropTypes.string,
    searchWrapperClasses: PropTypes.string,
    singleSelect: PropTypes.bool,
    name: PropTypes.string,
    searchCaption: PropTypes.string,
    middlePanel: PropTypes.element,
    valueOverride: PropTypes.bool,
    readOnlyList: PropTypes.array,
    onDataLoaded: PropTypes.func,
    uniqueKey: PropTypes.string,
  };

  static defaultProps = {
    headingLevel: 4,
    relationValueKeys: [],
    maxPageButtons: 5,
    pageSize: getPageSize(),
    showSelectOptions: true,
    searchChangeDelay: 1000,
    CheckboxComponent: CheckboxControl,
    searchCaption: '',
    searchWrapperClasses: 'full-width m-b-5',
  };

  constructor(props) {
    super(props);

    const initialState = {
      valueList: [],
      activePage: 1,
      totalCount: null,
      isLoading: false,
      valueOverride: false,
      prevSelectedValues: props.selectedValues ?? null,
    };
    this.state = addFormDataToState(this.props, initialState);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.valueOverride) {
      return addFormDataToState(props, state);
    }

    // Sync formData when selectedValues changes externally (e.g. removing a filter chip in the modal)
    if (props.selectedValues != null && !isEqual(props.selectedValues, state.prevSelectedValues)) {
      if (props.singleSelect) {
        return {
          formData: { selected: props.selectedValues },
          prevSelectedValues: props.selectedValues,
        };
      }

      if (Array.isArray(props.selectedValues)) {
        const clonedState = { ...state, formData: { ...state.formData } };
        const newState = addFormDataToState(props, clonedState);
        newState.prevSelectedValues = props.selectedValues;
        return newState;
      }
    }

    return null;
  }

  componentDidMount() {
    this.getData(this.props.filters);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.filters, prevProps.filters)) {
      this.getData(this.props.filters, true);
    }
  }

  getData = async (filters, resetPagination = false) => {
    const { dataUrl, dataInclude, pageSize, filterField, sortBy, authToken, defaultRelationData, queryFields } =
      this.props;
    const { activePage, search, formData } = this.state;
    const nextPage = resetPagination ? 1 : activePage;

    this.setState({ isLoading: true }, async () => {
      let query;
      if (queryFields && search) {
        query = {
          name: {
            term: search,
            fields: queryFields,
          },
        };
      }

      const { data, meta } = await getData(dataUrl, {
        authToken,
        includes: dataInclude,
        filters: {
          ...filters,
          ...(filterField && search && !queryFields ? { [filterField]: search } : undefined),
        },
        sorts: sortBy && [{ value: sortBy, direction: 'asc' }],
        page: { number: nextPage, size: pageSize },
        query,
      });

      const newFormData = { ...formData };
      if (defaultRelationData) {
        data.forEach(relation => {
          const key = relation.id;
          newFormData[key] = newFormData[key] || {};
          newFormData[key].relation = Object.assign({}, defaultRelationData(relation, key), newFormData[key].relation);
        });
      }

      if (this.props.onDataLoaded) this.props.onDataLoaded(data);

      this.setState({
        isLoading: false,
        formData: newFormData,
        totalCount: getTotalCount(meta?.total_count || data.length),
        valueList: data,
        valueOverride: false,
        activePage: nextPage,
      });
    });
  };

  itemCount = () => {
    const { formData } = this.state;

    if (!formData) return 0;

    return Object.values(formData).filter(value => value && value.checked).length;
  };

  selectedKeys = formData =>
    Object.entries(formData)
      .filter(([_, value]) => value && value.checked)
      .map(([key, _]) => key);

  clearAll = () => {
    const { formData } = this.state;

    const newFormData = Object.keys(formData || {}).reduce(
      (acc, key) => {
        acc[key].checked = false;
        return acc;
      },
      { ...formData }
    );

    this.handleChange(newFormData);
  };

  handleSelectAll = async () => {
    const { dataUrl, filters, dataEntity, sortBy, authToken } = this.props;
    const { formData, totalCount } = this.state;

    this.setState({ isLoading: true });
    const selectedIds = await selectAll({
      endpoint: dataUrl,
      filters,
      isChecked: true,
      totalCount,
      selectedIds: [],
      entity: dataEntity,
      sortValue: sortBy,
      authToken,
    });
    this.setState({ isLoading: false });

    const newFormData = selectedIds.reduce(
      (cache, value) => {
        cache[value] = cache[value] || {};
        cache[value].checked = true;
        return cache;
      },
      { ...formData }
    );

    this.handleChange(newFormData);
  };

  onValueChange = keyValue => {
    const [key, value] = Object.entries(keyValue)[0];
    const formData = { ...this.state.formData };
    set(formData, key, value);
    this.handleChange(formData);
  };

  handleChange = formData => {
    const { relationKey, onChange, valueOverride } = this.props;
    const { existingRelations } = this.state;
    const selectedKeys = this.selectedKeys(formData);
    const selectedKeyMap = selectedKeys.reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: true,
      }),
      {}
    );
    if (relationKey) {
      const activeRelations = selectedKeys.map(key =>
        Object.assign({ id: existingRelations[key] }, formData[key].relation)
      );
      const toDeleteRelations = Object.entries(existingRelations)
        .filter(([id, _]) => !selectedKeyMap[id])
        .map(([_, relationId]) => {
          return {
            id: relationId,
            _destroy: true,
          };
        });
      const relations = activeRelations.concat(toDeleteRelations);
      if (onChange) onChange(relations);
    } else {
      if (onChange) onChange(selectedKeys);
    }
    if (!valueOverride) {
      this.setState({ formData });
    }
  };

  handleSearch = value => {
    if (value?.search === this.state?.search) return false;
    this.setState({ ...value, activePage: 1 }, () => {
      this.getData(this.props.filters);
    });
  };

  clearSearch = () => {
    this.clearAll();
    this.setState({ valueOverride: true, search: '' }, () => this.getData(this.props.filters));
  };

  handlePageChange = eventKey => {
    this.setState(
      {
        activePage: eventKey,
      },
      () => {
        this.getData(this.props.filters);
      }
    );
  };

  handleRadioChange = event => {
    this.setState({
      formData: {
        selected: event.target.value,
      },
    });
    if (this.props.onRadioChange) {
      this.props.onRadioChange(event.target.value);
    }
  };

  noItemsChecked = () => {
    const { formData } = this.state;

    if (!Object.keys(formData || {}).length) {
      return true;
    }

    return !Object.values(formData || {}).some(val => val && val.checked);
  };

  renderCustomRowItem = (key, value, element) => {
    const { disabled, renderRow, singleSelect } = this.props;
    const { formData } = this.state;

    const isSelected = !!formData && (singleSelect ? formData.selected === key : (formData[key] || {}).checked);

    const row = renderRow(
      element,
      `${key}[relation]`,
      disabled || !(formData?.[key] || {}).checked,
      value,
      key,
      isSelected
    );

    return row || null;
  };

  renderItems = () => {
    const {
      valueKey,
      labelKey,
      labelFunction,
      dataValue,
      disabled,
      renderRow,
      CheckboxComponent,
      singleSelect,
      name,
      readOnlyList,
      dataObjectType,
      dataObjectAction,
      dataObjectAttribute,
      uniqueKey,
    } = this.props;
    const { valueList, formData } = this.state;

    if (!valueList.length) {
      return <div className="m-t-5 m-b-5 no-results">{i18n.t('No results found')}</div>;
    }

    return valueList.map(value => {
      const key = value[valueKey];
      const label = typeof labelFunction === 'function' ? labelFunction(value) : value[labelKey];
      const checked = Array.isArray(this.props[this.props.name])
        ? this.props[this.props.name].some(id => value.id === id)
        : false;

      const element = singleSelect ? (
        <Checkbox
          checkClassName="radio m-t-5 m-b-5"
          data-object-action={dataObjectAction}
          data-object-attribute={dataObjectAttribute}
          data-object-id={key}
          data-object-type={dataObjectType}
          isChecked={formData?.selected === key}
          isDisabled={disabled}
          key={key}
          labelClassName="m-l-10"
          name={name}
          onChange={this.handleRadioChange}
          type="radio"
          value={key}
        >
          {label}
        </Checkbox>
      ) : (
        <FieldGroup>
          <CheckboxComponent
            key={key}
            valueOverride={true}
            id={uniqueKey && `${uniqueKey}_${key}`}
            name={`${key}[checked]`}
            data-object-type={dataObjectType}
            data-object-id={key}
            data-object-attribute={dataObjectAttribute}
            data-object-action={dataObjectAction}
            disabled={disabled || (readOnlyList && readOnlyList.includes(key))}
            checked={checked}
          >
            {label}
          </CheckboxComponent>
        </FieldGroup>
      );

      return typeof renderRow === 'function' ? this.renderCustomRowItem(key, value, element) : element;
    });
  };

  renderHeader = () => {
    const { heading, headingLevel, showSelectOptions, singleSelect, dataObjectType, dataObjectAttribute } = this.props;

    return (
      <div className="checkbox-list-header">
        {heading ? this.renderHeadingLevel(`${heading} *`, headingLevel) : null}
        {!singleSelect && showSelectOptions && (
          <p>
            <span
              className="select-all"
              onClick={this.handleSelectAll}
              data-object-type={dataObjectType}
              data-object-attribute={dataObjectAttribute}
              data-object-action={`select-all-${dataObjectType}`}
            >
              {i18n.t('Select All')}
            </span>
            <span>
              {this.itemCount()} {i18n.t('Selected')}
            </span>
            <span
              className="clear-all"
              onClick={this.clearAll}
              data-object-type={dataObjectType}
              data-object-attribute={dataObjectAttribute}
              data-object-action={`clear-all-${dataObjectType}`}
            >
              {i18n.t('Clear')}
            </span>
          </p>
        )}
      </div>
    );
  };

  renderHeadingLevel = (content, level) => {
    if (!content) return null;
    switch (level) {
      case 1:
        return <h1>{content}</h1>;
      case 2:
        return <h2>{content}</h2>;
      case 3:
        return <h3>{content}</h3>;
      case 4:
        return <h4>{content}</h4>;
      case 5:
        return <h5>{content}</h5>;
      case 6:
        return <h6>{content}</h6>;
      default:
        return null;
    }
  };

  renderPagination = () => {
    const { pageSize } = this.props;
    const { totalCount, activePage } = this.state;

    return totalCount > pageSize ? (
      <div className="text-center">
        <Pagination
          activePage={activePage}
          onPageChange={this.handlePageChange}
          pageSize={pageSize}
          totalCount={totalCount}
        />
      </div>
    ) : null;
  };

  renderFooter = () => {
    const { renderClearFilters } = this.props;

    return (
      <>
        {this.renderPagination()}
        {!this.noItemsChecked() && !!renderClearFilters && renderClearFilters(this.clearAll)}
      </>
    );
  };

  renderSearch = () => {
    const { filterField, queryFields, searchCaption, dataObjectType, dataObjectAttribute, searchWrapperClasses } =
      this.props;
    if (filterField || queryFields) {
      const placeholder = `${i18n.t('Search')} ${searchCaption}`;
      return (
        <SearchComponent
          name="search"
          placeholder={placeholder}
          searchAction={this.handleSearch}
          wrapperClass={searchWrapperClasses}
          data-object-type={dataObjectType}
          data-object-attribute={dataObjectAttribute}
          data-object-action={`${dataObjectAttribute}-search`}
        />
      );
    }
  };

  renderBody = () => {
    const { formData, isLoading } = this.state;

    const content = isLoading ? (
      <ProgressSpinner />
    ) : (
      <FieldGroup className="withScroll" data={formData} onValueChange={this.onValueChange}>
        {this.renderItems()}
      </FieldGroup>
    );

    return (
      <div className="select-wrapper">
        {this.renderSearch()}
        {this.props.middlePanel}
        <div className="list-content">{content}</div>
      </div>
    );
  };

  render() {
    return (
      <div className={`checkbox-list ${this.props.className}`}>
        {this.renderHeader()}
        {this.renderBody()}
        {this.renderFooter()}
      </div>
    );
  }
}

function selectionKey(selection, relationKey) {
  if (!relationKey) return selection;
  return typeof selection === 'object' && selection !== null ? selection[relationKey] : selection;
}

function addFormDataToState(props, state) {
  const { selectedValues, singleSelect, relationKey, relationValueKeys } = props;
  const activeValues = relationKey
    ? selectedValues.filter(v => (typeof v === 'object' && v !== null ? !v._destroy : true))
    : selectedValues;
  if (state.formData && Object.keys(state.formData || {}).length) {
    Object.keys(state.formData || {}).forEach(key => {
      state.formData[key].checked = false;
    });
    activeValues.forEach(selection => {
      const key = selectionKey(selection, relationKey);
      if (state.formData[key]) {
        state.formData[key].checked = true;
      } else {
        state.formData[key] = { checked: true };
      }
    });
  } else {
    state.formData = singleSelect
      ? { selected: activeValues }
      : getFormDataFromSelectedValues(activeValues, relationKey, relationValueKeys);
  }
  if (relationKey) {
    state.existingRelations = activeValues.reduce((accumulator, selection) => {
      if (typeof selection === 'object' && selection !== null) {
        return {
          ...accumulator,
          [selection[relationKey]]: selection.id,
        };
      }
      return accumulator;
    }, {});
  }
  return state;
}

function getFormDataFromSelectedValues(selectedValues, relationKey, relationValueKeys) {
  return selectedValues.reduce((accumulator, selection) => {
    const valueObject = { checked: true };
    if (relationKey) {
      valueObject.relation = relationValueKeys.reduce(
        (relationObject, valueKey) => ({
          ...relationObject,
          [valueKey]: selection[valueKey],
        }),
        {}
      );
    }
    return {
      ...accumulator,
      [selectionKey(selection, relationKey)]: valueObject,
    };
  }, {});
}
